/**
 * Two-Step Equations with Fractions generator.
 *
 * Two-step equations where fractions appear as coefficients and/or constants.
 * Easy problems stay clean (small denominators, integer solutions); harder
 * levels allow fractional solutions and larger denominators. Built with the
 * shared equation base so correctness is validated the same way as every other
 * equation topic.
 *
 * Families:
 *   T1: (a/b) x + c = d           fractional coefficient
 *   T2: x/b + c = d               unit fractional coefficient
 *   T3: (a/b) x - c = d           fractional coefficient, subtraction
 *   T4: (a/b)(x + c) = d          fractional distribution (harder)
 */

import { Rational, rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { LinearExpr, LinearEquation } from "../engine/linear";
import type { Difficulty, GenerateContext, Generator, Question } from "./types";
import {
  EQUATION_VARIABLES,
  generateEquation,
  equationSignature,
  type BuiltEquation,
  type EquationTopicOptions,
} from "./equation-base";

const TOPIC = "two-step-fractions" as const;
const VERSION = "1.0.0";

type Family = "T1" | "T2" | "T3" | "T4";

interface Cfg {
  readonly families: readonly Family[];
  readonly denoms: readonly number[];
  readonly numMax: number;
  readonly constMax: number;
  /** Solutions are multiples of the coefficient denominator to stay clean. */
  readonly cleanSolutions: boolean;
  readonly solMin: number;
  readonly solMax: number;
  readonly pNeg: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["T1", "T2"], denoms: [2, 3, 4], numMax: 1, constMax: 8, cleanSolutions: true, solMin: 1, solMax: 8, pNeg: 0 },
  standard: { families: ["T1", "T2", "T3"], denoms: [2, 3, 4, 5], numMax: 3, constMax: 10, cleanSolutions: true, solMin: -8, solMax: 8, pNeg: 0.35 },
  hard: { families: ["T1", "T3", "T4"], denoms: [2, 3, 4, 5, 6], numMax: 5, constMax: 12, cleanSolutions: false, solMin: -10, solMax: 10, pNeg: 0.45 },
  challenge: { families: ["T3", "T4"], denoms: [2, 3, 4, 5, 6, 8], numMax: 7, constMax: 12, cleanSolutions: false, solMin: -12, solMax: 12, pNeg: 0.5 },
};

function fracLatex(r: Rational, v: string): string {
  if (r.isInteger()) {
    if (r.equals(Rational.ONE)) return v;
    if (r.equals(Rational.of(-1n))) return `-${v}`;
    return `${r.toLatex()}${v}`;
  }
  return `${r.toLatex()}${v}`;
}
function fracText(r: Rational, v: string): string {
  if (r.isInteger()) {
    if (r.equals(Rational.ONE)) return v;
    if (r.equals(Rational.of(-1n))) return `-${v}`;
    return `${r.toString()}${v}`;
  }
  // No "*" - the reparser reads "2/3x" as coefficient 2/3 times x.
  return `${r.toString()}${v}`;
}
function plusL(r: Rational): string {
  return r.isNegative() ? `- ${r.neg().toLatex()}` : `+ ${r.toLatex()}`;
}
function plusT(r: Rational): string {
  return r.isNegative() ? `- ${r.neg().toString()}` : `+ ${r.toString()}`;
}

/** A genuinely fractional coefficient (never reduces to an integer). */
function chooseCoeff(rng: Rng, cfg: Cfg): Rational | null {
  const den = rng.pick(cfg.denoms);
  const num = rng.int(1, cfg.numMax) * rng.sign(cfg.pNeg);
  const r = rat(num, den);
  return r.isInteger() ? null : r; // reject integer coefficients for this topic
}

/** Choose a solution; when cleanSolutions, make it a multiple of the denominator. */
function chooseSolution(rng: Rng, cfg: Cfg, coeff: Rational): Rational {
  if (cfg.cleanSolutions) {
    // Multiple of the coefficient's denominator so coeff*sol is an integer.
    const mult = rng.intNonZero(cfg.solMin, cfg.solMax);
    return rat(BigInt(mult) * coeff.d);
  }
  return rat(rng.intNonZero(cfg.solMin, cfg.solMax));
}

function build(rng: Rng, difficulty: Difficulty): BuiltEquation | null {
  const cfg = DIFF[difficulty];
  const v = rng.pick(EQUATION_VARIABLES);
  const family = rng.pick(cfg.families);
  const konst = () => rat(rng.int(1, cfg.constMax) * rng.sign(cfg.pNeg));

  if (family === "T2") {
    // x/b + c = d  ->  coefficient 1/b
    const b = rng.pick(cfg.denoms);
    const coeff = rat(1, b);
    const s = chooseSolution(rng, cfg, coeff);
    const c = konst();
    const left = new LinearExpr(coeff, c);
    const d = left.evaluate(s);
    const eq = new LinearEquation(left, LinearExpr.constantExpr(d));
    if (!d.isInteger() && difficulty === "easy") return null;
    // Latex shows x/b for the student; displayText uses the coefficient form
    // (1/b x) which the reparser understands.
    return mk(
      family,
      v,
      `\\frac{${v}}{${b}} ${plusL(c)} = ${d.toLatex()}`,
      `${coeff.toString()}${v} ${plusT(c)} = ${d.toString()}`,
      eq,
      s,
    );
  }

  if (family === "T4") {
    // (a/b)(x + c) = d
    const coeff = chooseCoeff(rng, cfg);
    if (!coeff) return null;
    const s = chooseSolution(rng, cfg, coeff);
    const c = konst();
    const left = LinearExpr.variable().add(LinearExpr.constantExpr(c)).scale(coeff);
    const d = left.evaluate(s);
    const eq = new LinearEquation(left, LinearExpr.constantExpr(d));
    return mk(
      family,
      v,
      `${coeff.toLatex()}(${v} ${plusL(c)}) = ${d.toLatex()}`,
      `${coeff.toString()}(${v} ${plusT(c)}) = ${d.toString()}`,
      eq,
      s,
    );
  }

  // T1 / T3: (a/b) x +/- c = d
  const coeff = chooseCoeff(rng, cfg);
  if (!coeff) return null;
  const s = chooseSolution(rng, cfg, coeff);
  const c = konst();
  const constant = family === "T3" ? c.neg().abs().neg() : c; // T3 forces subtraction visual
  const left = new LinearExpr(coeff, constant);
  const d = left.evaluate(s);
  const eq = new LinearEquation(left, LinearExpr.constantExpr(d));
  return mk(
    family,
    v,
    `${fracLatex(coeff, v)} ${plusL(constant)} = ${d.toLatex()}`,
    `${fracText(coeff, v)} ${plusT(constant)} = ${d.toString()}`,
    eq,
    s,
  );
}

function mk(
  family: Family,
  v: string,
  displayLatex: string,
  displayText: string,
  eq: LinearEquation,
  s: Rational,
): BuiltEquation {
  return {
    family,
    variable: v,
    displayLatex,
    displayText,
    equation: eq,
    solution: s,
    leadingHints: ["Undo addition or subtraction first, then multiply by the reciprocal of the coefficient."],
  };
}

function maxMagnitude(difficulty: Difficulty): bigint {
  const m: Record<Difficulty, number> = { easy: 60, standard: 120, hard: 220, challenge: 320 };
  return BigInt(m[difficulty]);
}

const OPTIONS: EquationTopicOptions = {
  topic: TOPIC,
  version: VERSION,
  maxMagnitude,
  build,
  // Avoid a degenerate "= 0" right-hand side which reads oddly for this topic.
  quality: (b) => !b.equation.right.constant.isZero(),
};

export class TwoStepFractionsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = ["easy", "standard", "hard", "challenge"];
  generate(ctx: GenerateContext): Question {
    return generateEquation(OPTIONS, ctx);
  }
  signature(q: Question) {
    return equationSignature(TOPIC, q);
  }
}

export const twoStepFractions = new TwoStepFractionsGenerator();
